import http from "@services/httpService"

/**
 * Dashboard Kế toán / ACC (Feature-0198). Backend: `FinanceDashboardAppService`
 * (`api/services/app/FinanceDashboard/*`). Mọi method là read-only.
 */

/** Widget W-FIN-01 — một điểm cho mỗi tháng trên biểu đồ thu vs kế hoạch. */
export interface ICollectionVsPlanMonth {
  /** Ngày đầu tháng theo giờ Việt Nam; FE chỉ dùng để hiện nhãn tháng. */
  month: string
  /** Thực thu trong tháng — khớp báo cáo Daily Collection cùng tháng. */
  actualAmount: number
  /** Kế hoạch thu trong tháng — khớp báo cáo Due Date cùng tháng. */
  planAmount: number
  /** `null` khi tháng đó không có kế hoạch thu — KHÁC "đạt 0%". */
  collectionRate: number | null
}

/** Widget W-FIN-02 — một ô (tháng × dự án) của dòng tiền dự kiến 90 ngày. */
export interface ICashflowMonth {
  /** Ngày đầu tháng theo giờ Việt Nam. */
  month: string
  projectId: number
  projectName?: string
  /** Phần chưa thu của các kỳ đến hạn trong tháng. */
  expectedAmount: number
}

/** Widget W-ACC-09 — kỳ thanh toán đến hạn / tất toán / còn treo. */
export interface IBilledSettlement {
  billedCount: number
  billedAmount: number
  settledCount: number
  settledAmount: number
  outstandingCount: number
  outstandingAmount: number
  overdueCount: number
  overdueAmount: number
  /** Kỳ hoàn tiền, TÁCH RIÊNG khỏi phải-thu. */
  refundPeriodCount: number
  refundPeriodAmount: number
}

/** Widget W-ACC-08 — sức khoẻ đồng bộ trong kỳ. */
export interface IIntegrationHealth {
  totalExecutions: number
  successCount: number
  failCount: number
  successRate: number
  dlqCount: number
  /** XẤP XỈ: thất bại chưa vào dead-letter. Hệ thống không lưu cờ "đang chờ retry". */
  pendingRetryCount: number
  failuresByCategory: Record<string, number>
  /** Cửa sổ THỰC TẾ sau khi clamp 90 ngày. */
  windowFromDate: string
  windowToDate: string
}

/** Widget W-ACC-07 — một hợp đồng trong top lãi treo. */
export interface IInterestOutstandingRow {
  contractId: number
  contractCode?: string
  /** Tên đã được backend MASK. */
  customerName?: string
  amount: number
}

/**
 * Widget W-ACC-07 — lãi treo + đã thu/miễn giảm.
 * CỐ Ý không có "lãi phát sinh trong kỳ": hệ thống không lưu lãi tích luỹ, con số đó chỉ là xấp xỉ.
 */
export interface IInterestSummary {
  outstandingInterestTotal: number
  contractsWithInterestCount: number
  collectedInPeriod: number
  waivedInPeriod: number
  topOutstanding: IInterestOutstandingRow[]
  /** Vượt trần quét ⇒ tổng đang THIẾU, widget phải cảnh báo. */
  isTruncated: boolean
}

/** Widget W-ACC-06 — số lượng + giá trị phiếu theo một loại. */
export interface IReceiptByType {
  /** Giá trị enum `Module` (135 BOOKING_RECEIPT, 136 PLAN_RECEIPT, …). */
  objectType: number
  count: number
  /** Giữ NGUYÊN DẤU — phiếu điều chỉnh âm hiện âm. */
  amount: number
}

/** Widget W-ACC-06 — phiếu thu & chất lượng phân bổ của kỳ. */
export interface IReceiptSummary {
  byType: IReceiptByType[]
  unallocatedCount: number
  unallocatedAmount: number
  partiallyAllocatedCount: number
  adjustmentCount: number
  adjustmentAmount: number
}

/** Widget W-FIN-05 — một tháng của biểu đồ doanh thu ghi nhận. */
export interface IRevenueMonth {
  /** Ngày đầu tháng theo giờ Việt Nam. */
  month: string
  contractCount: number
  /** Giá trị hợp đồng ghi nhận, theo trục `ValidDate` (KHÁC `SignedDate`). */
  recognisedAmount: number
}

/** Widget W-FIN-05 — phần phải thu của kỳ đang lọc. */
export interface IDebitNoteSummary {
  contractCount: number
  billedAmount: number
  outstandingAmount: number
  revenueTrend: IRevenueMonth[]
}

/** Widget W-FIN-04 — một dòng của bảng top nợ quá hạn. */
export interface ITopOverdue {
  contractId: number
  contractCode?: string
  /** Tên đã được backend MASK — FE không nhận tên đầy đủ ở widget này. */
  customerName?: string
  overdueAmount: number
  /** `OverDue30` | `OverDue60` | `OverDue90` | `OverDueMoreThan90`. */
  worstBucket?: string
}

/** Widget W-FIN-03 — một điểm aging theo tháng. */
export interface IAgingSnapshot {
  snapshotDate: string
  bucket30: number
  bucket60: number
  bucket90: number
  bucketOver90: number
  total: number
  /** Điểm bị cắt vì vượt trần quét ⇒ số ĐANG THIẾU, widget phải cảnh báo. */
  isTruncated: boolean
}

class DashboardFinanceService {
  public async getCollectionVsPlan(
    params?: Record<string, unknown>
  ): Promise<ICollectionVsPlanMonth[]> {
    const res = await http.get(
      "api/services/app/FinanceDashboard/GetCollectionVsPlan",
      { params }
    )
    return res.data.result
  }

  public async getBilledSettlement(
    params?: Record<string, unknown>
  ): Promise<IBilledSettlement> {
    const res = await http.get(
      "api/services/app/FinanceDashboard/GetBilledSettlement",
      { params }
    )
    return res.data.result
  }

  public async getIntegrationHealth(
    params?: Record<string, unknown>
  ): Promise<IIntegrationHealth> {
    const res = await http.get(
      "api/services/app/FinanceDashboard/GetIntegrationHealth",
      { params }
    )
    return res.data.result
  }

  public async getInterestSummary(
    params?: Record<string, unknown>
  ): Promise<IInterestSummary> {
    const res = await http.get(
      "api/services/app/FinanceDashboard/GetInterestSummary",
      { params }
    )
    return res.data.result
  }

  public async getReceiptSummary(
    params?: Record<string, unknown>
  ): Promise<IReceiptSummary> {
    const res = await http.get(
      "api/services/app/FinanceDashboard/GetReceiptSummary",
      { params }
    )
    return res.data.result
  }

  public async getDebitNoteSummary(
    params?: Record<string, unknown>
  ): Promise<IDebitNoteSummary> {
    const res = await http.get(
      "api/services/app/FinanceDashboard/GetDebitNoteSummary",
      { params }
    )
    return res.data.result
  }

  public async getTopOverdue(
    params?: Record<string, unknown>
  ): Promise<ITopOverdue[]> {
    const res = await http.get(
      "api/services/app/FinanceDashboard/GetTopOverdue",
      { params }
    )
    return res.data.result
  }

  public async getArAgingTrend(
    params?: Record<string, unknown>
  ): Promise<IAgingSnapshot[]> {
    const res = await http.get(
      "api/services/app/FinanceDashboard/GetArAgingTrend",
      { params }
    )
    return res.data.result
  }

  public async getCashflowForecast(
    params?: Record<string, unknown>
  ): Promise<ICashflowMonth[]> {
    const res = await http.get(
      "api/services/app/FinanceDashboard/GetCashflowForecast",
      { params }
    )
    return res.data.result
  }
}

export default new DashboardFinanceService()
